import React from 'react';

const PaginationControls = ({ currentPage, totalPages, onPageChange, className = "", showPageNumbers = true }) => {
  // Não renderizar se apenas uma página
  if (totalPages <= 1) {
    return null;
  }

  const handlePageChange = (page) => {
    // Evitar navegação para fora dos limites
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  // Determinar quais botões de página mostrar
  const getPageNumbersToDisplay = () => {
    const pageNumbers = [];
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);
    
    // Garantir que sempre tenhamos até 5 botões se possível
    if (endPage - startPage < 4) {
      if (currentPage < totalPages / 2) {
        endPage = Math.min(startPage + 4, totalPages);
      } else {
        startPage = Math.max(1, endPage - 4);
      }
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    
    return pageNumbers;
  };

  const pageNumbers = getPageNumbersToDisplay();

  return (
    <div className={`flex justify-center items-center space-x-2 my-4 ${className}`}>
      {/* Botão para primeira página */}
      <button
        onClick={() => handlePageChange(1)}
        disabled={currentPage === 1}
        className={`px-3 py-1 border rounded ${
          currentPage === 1
            ? 'border-gray-200 text-gray-400 cursor-not-allowed'
            : 'border-gray-300 hover:bg-gray-100'
        }`}
        aria-label="Primeira página"
      >
        &laquo;
      </button>

      {/* Botão anterior */}
      <button
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`px-3 py-1 border rounded ${
          currentPage === 1
            ? 'border-gray-200 text-gray-400 cursor-not-allowed'
            : 'border-gray-300 hover:bg-gray-100'
        }`}
        aria-label="Página anterior"
      >
        &lsaquo;
      </button>

      {/* Botões com números de página */}
      {showPageNumbers && (
        <>
          {pageNumbers[0] > 1 && (
            <>
              <button
                onClick={() => handlePageChange(1)}
                className="px-3 py-1 border rounded border-gray-300 hover:bg-gray-100"
              >
                1
              </button>
              {pageNumbers[0] > 2 && (
                <span className="px-2 py-1">...</span>
              )}
            </>
          )}

          {pageNumbers.map(number => (
            <button
              key={number}
              onClick={() => handlePageChange(number)}
              className={`px-3 py-1 border rounded ${
                number === currentPage
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'border-gray-300 hover:bg-gray-100'
              }`}
            >
              {number}
            </button>
          ))}

          {pageNumbers[pageNumbers.length - 1] < totalPages && (
            <>
              {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
                <span className="px-2 py-1">...</span>
              )}
              <button
                onClick={() => handlePageChange(totalPages)}
                className="px-3 py-1 border rounded border-gray-300 hover:bg-gray-100"
              >
                {totalPages}
              </button>
            </>
          )}
        </>
      )}

      {/* Informação simplificada (alternativa aos números) */}
      {!showPageNumbers && (
        <span className="px-3 py-1 text-center">
          Página {currentPage} de {totalPages}
        </span>
      )}

      {/* Botão próximo */}
      <button
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`px-3 py-1 border rounded ${
          currentPage === totalPages
            ? 'border-gray-200 text-gray-400 cursor-not-allowed'
            : 'border-gray-300 hover:bg-gray-100'
        }`}
        aria-label="Próxima página"
      >
        &rsaquo;
      </button>

      {/* Botão para última página */}
      <button
        onClick={() => handlePageChange(totalPages)}
        disabled={currentPage === totalPages}
        className={`px-3 py-1 border rounded ${
          currentPage === totalPages
            ? 'border-gray-200 text-gray-400 cursor-not-allowed'
            : 'border-gray-300 hover:bg-gray-100'
        }`}
        aria-label="Última página"
      >
        &raquo;
      </button>
    </div>
  );
};

export default PaginationControls;
